import { computed, action } from 'mobx'
import {
  catchError,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  map,
  filter,
  switchMap,
} from 'rxjs'
import { task } from 'mobx-task'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import type { CursorPageQuery } from '@/infra/paging'
import {
  type AdminPayoutHistoryStore,
  type ListHistoricalTransactionsQuery,
} from '@/features/payouts/admin/AdminPayoutHistoryStore'
import { type AdminTransaction } from '@/features/payouts/admin/AdminTransaction'
import { type GetCurrentUserFn } from '@/features/iam'
import { type StreamDownloader } from '@/features/payouts/stream-downloader'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type DateRange, DateRangeFilter } from '@/infra/DateRangeFilter'
import { FiltersViewModel, FilterItemSource } from '@/infra/FiltersViewModel'
import { SelectFilter } from '@/infra/SelectFilter'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'

/**
 * Historical transactions query, without paging.
 */
type HistoricalTransactionsQuery = Omit<
  ListHistoricalTransactionsQuery,
  keyof CursorPageQuery
>
/**
 * Filter for historical transactions.
 */
export interface AdminTransactionHistoryFilter {
  completedRange: DateRange
  flags: TransactionFlag[]
}

/**
 * View model for the admin transaction history view.
 */
export class AdminTransactionHistoryViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The query to use.
   */
  private readonly _query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ]),
  ).pipe(
    map(([searchText, filters]): HistoricalTransactionsQuery => ({
      searchText,
      dateCompleted: filters.completedRange,
      flags: filters.flags,
    })),
    shareLatest(),
  )

  /**
   * Fetch pipeline.
   */
  private readonly _fetch$ = this._query$.pipe(
    filter(() => this.canAccessPayoutsAdministration),
    switchMap((query) =>
      withAbortSignal((signal) =>
        this.transactionsScroller.fetch({ filter: query, signal }),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<AdminTransactionHistoryFilter>({
    definitions: {
      completedRange: {
        icon: 'date-completed',
        title: 'Date Paid',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      flags: {
        icon: 'flags',
        title: 'Flags',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'Job canceled',
                value: 'JobCanceled',
              },
              {
                title: 'Split auto-adjusted',
                value: 'JobPayoutAutoAdjusted',
              },
            ]),
            true,
          ),
      },
    },
  })

  /**
   * Scroller for transactions.
   */
  readonly transactionsScroller = new InfiniteScrollProvider<
    AdminTransaction,
    HistoricalTransactionsQuery
  >({
    limit: 200,
    fetchItems: ({ paging, signal, filter = {} }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load transactions',
        signal,
      )

      return withRetry(() =>
        this._adminPayoutHistoryStore.listHistoricalTransactions(
          { ...filter, ...paging },
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _adminPayoutHistoryStore: AdminPayoutHistoryStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _downloader: StreamDownloader,
  ) {
    this._lifecycle.manage(this._fetch$)
    this._lifecycle.manage(this.filters)
  }

  /**
   * Gets the total count of transactions that match the server-based filter.
   */
  @computed
  get transactionsCount() {
    return this.transactionsScroller.total
  }

  /**
   * Whether the user can access the payouts administration.
   */
  @computed
  get canAccessPayoutsAdministration() {
    return this._getCurrentUser().platformAdmin
  }

  /**
   * The transactions that have been loaded.
   */
  @computed
  get transactions() {
    return this.transactionsScroller.items
  }

  /**
   * Whether the transactions table shows, rather than the empty state.
   */
  @computed
  get showTable() {
    return (
      this.transactionsScroller.fetch.pending || this.transactions.length > 0
    )
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  @computed
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Pages through all transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const handle = await this._downloader.requestDownload({
      suggestedName: 'transaction-history.csv',
      fileTypeDescription: 'CSV file',
      accept: { 'text/csv': ['.csv'] },
    })
    if (!handle) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Exporting', 'This may take a moment.')
      .show()
    try {
      // Get the query that is used for the current list.
      const query = await firstValueFrom(this._query$)

      // Download the historical transactions, pipe them to the file.
      await this._adminPayoutHistoryStore
        .downloadHistoricalTransactionsAsCsv({
          workspaceIds: query.workspaceIds,
          dateCompleted: query.dateCompleted,
          searchText: query.searchText,
          recipientUserId: query.recipientUserId,
          flags: query.flags,
        })
        .then((csv) => handle.download(csv))

      alert
        .withState('success')
        .saying(
          'Export completed!',
          `The transaction history was exported to ${handle.fileName}.`,
        )
        .autoDismiss()
    } catch (err) {
      alert.dismiss()
      return this._createAlertErrorHandler('Exporting failed')(err)
    }
  })
}
